import { createRoot } from "react-dom/client"
import { App } from "./App"
import "./i18n"
import * as Sentry from "@sentry/react"
import { GoogleOAuthProvider } from "@react-oauth/google"
import { routes } from "common/navigationRoutes"
import AuthProvider from "contexts/AuthContext"
import StudentAuthProvider from "contexts/StudentAuthContext"
import { ThemeProvider } from "themes/ThemeProvider"
import { BrowserRouter } from "react-router-dom"
import GlobalAlertProvider from "contexts/GlobalAlertContext"
import React, { Suspense } from "react"

const PiecePage = React.lazy(() =>
  import(/* webpackChunkName: "avatarPiece" */ "pages/Auth/Avatar/PiecePage")
)
const MyAvatarPage = React.lazy(() =>
  import(
    /* webpackChunkName: "myAvatarPage" */ "pages/Auth/Avatar/MyAvatarPage"
  )
)

const RewardsItem = React.lazy(() =>
  import(
    /* webpackChunkName: "rewardsItem" */ "pages/student/rewards/RewardsItem"
  )
)

// Verificar la URL actual
const currentPath = window.location.pathname?.toLowerCase()

// routes modals
if (
  currentPath === routes.REDWARDS_ITEM?.toLowerCase() ||
  currentPath === process.env.REACT_APP_MS_REDIRECT_URI + routes.REDWARDS_ITEM?.toLowerCase()
) {
  const container = document.getElementById("root")
  const root = createRoot(container)
  root.render(
    <ThemeProvider>
      <GlobalAlertProvider>
        <BrowserRouter
          basename={document.querySelector("base")?.getAttribute("href") ?? "/"}
        >
          <AuthProvider>
            <StudentAuthProvider>
              <Suspense fallback={<div>...</div>}>
                <RewardsItem />
              </Suspense>
            </StudentAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </GlobalAlertProvider>
    </ThemeProvider>
  )
} else {
  if (
    currentPath === routes.AVATAR_PIECE?.toLowerCase() ||
    currentPath === routes.MY_AVATAR?.toLowerCase() ||
    currentPath === process.env.REACT_APP_MS_REDIRECT_URI + routes.AVATAR_PIECE?.toLowerCase() ||
    currentPath === process.env.REACT_APP_MS_REDIRECT_URI + routes.MY_AVATAR?.toLowerCase()
  ) {
    // Renderizar PiecePage directamente si la URL coincide
    const container = document.getElementById("root")
    const root = createRoot(container)
    root.render(
      currentPath === routes.MY_AVATAR?.toLowerCase() ||
        currentPath === process.env.REACT_APP_MS_REDIRECT_URI + routes.MY_AVATAR?.toLowerCase() ? (
        <div>
          <Suspense fallback={<div>Loading...</div>}>
            <MyAvatarPage />
          </Suspense>
        </div>
      ) : (
        <div>
          <Suspense fallback={<div>Loading...</div>}>
            <PiecePage />
          </Suspense>
        </div>
      )
    )
  } else {
    if (process.env.REACT_APP_ENVIORNMENT !== "development") {
      Sentry.init({
        dsn: "https://6c1e8639d8670f84570e949cec2c2508@o4507091823951872.ingest.us.sentry.io/4507119921659904",
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1, //  Capture 1 -> 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/yourserver\.io\/api/,
        ],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        release: `${process.env.REACT_APP_ENVIORNMENT} - ${process.env.REACT_APP_VERSION}`,
        ignoreErrors: [
          "NotAllowedError",
          "ResizeObserver loop limit exceeded",
          "ResizeObserver loop completed with undelivered notifications",
          "TypeError: Failed to fetch",
          "TypeError: NetworkError when attempting to fetch resource.",
          "TypeError: Cancelled",
          "TypeError: cancelado",
          "AbortError",
          "AbortError: The play() request was interrupted because the media was removed from the document.",
          "Loading chunk",
          "AbortError: The operation was aborted.",
          "InvalidStateError",
          "AbortError: The play() request was interrupted by a call to pause().",
          "NotSupportedError",
          "NotSupportedError: The element has no supported sources",
          "NotSupportedError: The media resource indicated by the src attribute or assigned media provider object was not suitable.",
          "NotSupportedError: The operation is not supported.",
          "AbortError: The fetching process for the media resource was aborted by the user agent at the user's request.",
          "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
          "Uncaught TypeError: Cannot read properties of null (reading 'command')",
          "Uncaught TypeError: Cannot read property 'command' of null",
          "Target container is not a DOM element",
          "Loading CSS chunk",
          "Failed to execute 'insertBefore' on 'Node'",
          "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.",
          "a.domNode.getBoundingClientRect is not a function",
          "Can't find variable: ResizeObserver",
          "Cannot read properties of null (reading 'setPlaybackRate')",
          "IndexSizeError: The index is not in the allowed range.",
          "Failed to execute 'setStart' on 'Range':",
          "IndexSizeError",
          "The provided value is non-finite",
          "setValueAtTime([native code])",
          "Array buffer allocation failed",
          "Target container is not a DOM element.",
          "Error: Target container is not a DOM element.",
          "Error: Target container is not a DOM element.Array.forEach(<anonymous>)",
          "null is not an object (evaluating 'e.ctx.suspend')",
          "The object can not be found here.",
          "NotFoundError",
          "Cannot read properties of null (reading 'getBoundingClientRect')",
        ],
        beforeSend(event) {
          // console.log("Sentry event", event.request?.url)
          // Ignore events with "/activities-streaks" in the request URL
          if (event.request?.url?.includes("/activities-streaks")) {
            return null // Drop the event
          }

          // Filter out errors by message and stack trace
          const errorMessage = event.exception?.values?.[0]?.value || ""
          const stackTrace =
            event.exception?.values?.[0]?.stacktrace?.frames || []
          const stackTraceString = JSON.stringify(stackTrace)

          // Filter out "Target container is not a DOM element" errors
          // Check both message and if it comes from sweetalert2-react-content
          if (
            errorMessage.includes("Target container is not a DOM element") ||
            (errorMessage.includes("not a DOM element") &&
              stackTraceString.includes("sweetalert2"))
          ) {
            return null // Drop the event
          }

          // Filter out SignalR handshake cancellation errors
          if (errorMessage.includes("Handshake was canceled")) {
            return null // Drop the event
          }

          // Filter out SignalR stop while connecting errors
          if (errorMessage.includes("still in the connecting state")) {
            return null // Drop the event
          }

          // Filter out cross-origin frame access errors (SecurityError)
          if (
            errorMessage.includes("Blocked a frame with origin") ||
            errorMessage.includes("cross-origin frame") ||
            (errorMessage.includes("SecurityError") &&
              errorMessage.includes("openFontOptions"))
          ) {
            return null // Drop the event
          }

          return event // Send the event to Sentry
        },
      })
    }

    const AppProvider = () => (
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <App />
      </GoogleOAuthProvider>
    )
    const container = document.getElementById("root")
    const root = createRoot(container)
    root.render(<AppProvider />)
  }
}
